import React, { useMemo, useState } from 'react';
import { Package, Clock, CheckCircle, XCircle, Search } from 'lucide-react';
import { useOrders } from '../contexts/OrderContext';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
export function MyOrdersPage() {
  const {
    orders
  } = useOrders();
  const {
    user
  } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const myOrders = useMemo(() => {
    if (!user) return [];
    return orders.filter(order => order.employeeId === user.id).filter(order => {
      if (!searchTerm) return true;
      return order.orderNo.toLowerCase().includes(searchTerm.toLowerCase()) || order.product.toLowerCase().includes(searchTerm.toLowerCase());
    }).sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());
  }, [orders, user, searchTerm]);
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Pendiente':
        return <Clock className="w-5 h-5 text-yellow-500" data-id="element-2028" />;
      case 'En Preparación':
        return <Package className="w-5 h-5 text-blue-500" data-id="element-2029" />;
      case 'Listo':
        return <CheckCircle className="w-5 h-5 text-green-500" data-id="element-2030" />;
      case 'Entregado':
        return <CheckCircle className="w-5 h-5 text-gray-400" data-id="element-2031" />;
      case 'Cancelado':
        return <XCircle className="w-5 h-5 text-red-500" data-id="element-2032" />;
      default:
        return <Package className="w-5 h-5 text-gray-400" data-id="element-2033" />;
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Pendiente':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'En Preparación':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'Listo':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'Entregado':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
      case 'Cancelado':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };
  return <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 md:p-6" data-id="element-2034">
      <div className="max-w-4xl mx-auto" data-id="element-2035">
        <div className="mb-6" data-id="element-2036">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-2037">
            Mis Pedidos
          </h1>
          <p className="text-gray-600 dark:text-gray-400" data-id="element-2038">
            Historial de tus pedidos realizados
          </p>
        </div>

        <div className="mb-6" data-id="element-2039">
          <div className="relative" data-id="element-2040">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" data-id="element-2041" />
            <input type="text" placeholder="Buscar por número de orden o producto..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white" data-id="element-2042" />
          </div>
        </div>

        {myOrders.length === 0 ? <Card className="p-12 text-center" data-id="element-2043">
            <Package className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" data-id="element-2044" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2" data-id="element-2045">
              No tienes pedidos
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6" data-id="element-2046">
              Tus pedidos aparecerán aquí una vez que realices uno
            </p>
          </Card> : <div className="space-y-4" data-id="element-2047">
            {myOrders.map(order => <Card key={order.id} className="p-4 md:p-6 hover:shadow-md transition-shadow" data-id="element-2048">
                <div className="flex items-start justify-between mb-4" data-id="element-2049">
                  <div className="flex items-center gap-3" data-id="element-2050">
                    {getStatusIcon(order.status)}
                    <div data-id="element-2051">
                      <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-2052">
                        Orden #{order.orderNo}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2053">
                        {order.orderDate}
                      </p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`} data-id="element-2054">
                    {order.status}
                  </span>
                </div>

                <div className="space-y-2 mb-4" data-id="element-2055">
                  <div className="flex justify-between text-sm" data-id="element-2056">
                    <span className="text-gray-600 dark:text-gray-400" data-id="element-2057">
                      Producto:
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white" data-id="element-2058">
                      {order.product}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-2059">
                    <span className="text-gray-600 dark:text-gray-400" data-id="element-2060">
                      Empresa:
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white" data-id="element-2061">
                      {order.company}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-2062">
                    <span className="text-gray-600 dark:text-gray-400" data-id="element-2063">
                      Sucursal:
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white" data-id="element-2064">
                      {order.branch}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-2065">
                    <span className="text-gray-600 dark:text-gray-400" data-id="element-2066">
                      Tipo:
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white" data-id="element-2067">
                      {order.catalogType}
                    </span>
                  </div>
                </div>

                {order.notes && <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg" data-id="element-2068">
                    <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-2069">
                      <span className="font-medium" data-id="element-2070">Notas:</span> {order.notes}
                    </p>
                  </div>}
              </Card>)}
          </div>}
      </div>
    </div>;
}